/**
 * Reloads the app when a newer build is deployed.
 *
 * Opsyri is an installed PWA ("display": "standalone"), and iOS caches a
 * standalone app's start URL hard enough that it can keep serving an old
 * index.html — which points at old /assets hashes that we serve as immutable
 * for a year. The result is a phone pinned to a stale bundle indefinitely,
 * with fixes appearing to "not deploy".
 *
 * This is deliberately NOT caching logic. It only ever forces the app
 * forward. The service worker (vite-plugin-pwa, see vite.config.ts) precaches
 * the build so the app opens with no service; on a new deploy this hook
 * unregisters it, clears its caches and reloads, and the fresh shell
 * re-registers a worker for the new build.
 */
import { useEffect, useRef } from 'react'

declare const __BUILD_ID__: string

const CHECK_INTERVAL_MS = 10 * 60 * 1000

/** Never interrupt someone mid-sentence or mid-decision. */
function safeToReload(): boolean {
  const el = document.activeElement as HTMLElement | null
  if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
    return false
  }
  // An open dialog means an unfinished task.
  return !document.querySelector('[role="dialog"], [role="alertdialog"]')
}

const RELOAD_PARAM = '_r'

/**
 * Reloading the same URL is not enough on an installed iOS PWA: the start URL
 * document is served from the standalone cache, so we would come back with
 * the same stale index.html. Drop any service worker + Cache Storage, then
 * navigate to a URL the cache has never seen so the shell is refetched.
 */
async function forceFreshLoad(buildId: string) {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)))
    }
  } catch {
    /* best effort */
  }
  const url = new URL(window.location.href)
  url.searchParams.set(RELOAD_PARAM, buildId)
  // Replace, not reload: drops the current entry so a back gesture can't
  // land on the stale document.
  window.location.replace(url.toString())
}

/** Remove the cache-busting param once the fresh shell has loaded. */
function stripReloadParam() {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(RELOAD_PARAM)) return
  url.searchParams.delete(RELOAD_PARAM)
  window.history.replaceState(window.history.state, '', url.toString())
}

export function useBuildVersionCheck() {
  const reloading = useRef(false)

  useEffect(() => {
    let cancelled = false
    stripReloadParam()

    const check = async () => {
      if (cancelled || reloading.current || document.hidden) return
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) return
        const { buildId } = await res.json()
        if (!buildId || buildId === __BUILD_ID__) return
        if (!safeToReload()) return
        reloading.current = true
        await forceFreshLoad(buildId)
      } catch {
        /* offline or blocked — try again next tick */
      }
    }

    // On return-to-foreground is when a phone is most likely to be stale, and
    // it's the moment the user is least likely to lose anything.
    const onVisible = () => { if (!document.hidden) check() }

    check()
    const timer = window.setInterval(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}
